import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Waves, Warehouse, HardHat, Siren, Droplets, CreditCard, Banknote,
  ArrowLeft, Loader2, MapPin, Calendar, User, XCircle, Minus, Plus, CheckCircle2, Search, Info, Gift,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

const SERVICES = [
  { type: "pool", label: "Pool Filling", icon: Waves },
  { type: "tank", label: "Tank Refill", icon: Warehouse },
  { type: "construction", label: "Construction", icon: HardHat },
  { type: "emergency", label: "Emergency", icon: Siren },
];
const WATER = [
  { type: "town", label: "Town Water", note: "Standard tanker supply" },
  { type: "pure", label: "Pure Water", note: "Premium filtered supply" },
];

const CLOSED_LABEL = {
  sunday: "Closed Sundays",
  holiday: "Public holiday",
  cutoff: "Cut-off passed",
  timeup: "No time left today",
  blocked: "Closed",
  past: "Past",
  full: "Fully booked",
};

export default function Book() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [service, setService] = useState(params.get("service") || "pool");
  const [waterType, setWaterType] = useState("town");
  const [loads, setLoads] = useState(1);
  const [payment, setPayment] = useState("card");
  const [loading, setLoading] = useState(false);
  const [zone, setZone] = useState(null); // {suburb, town_water, pure_water}
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [avail, setAvail] = useState(null);
  const [days, setDays] = useState([]);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [pureEnabled, setPureEnabled] = useState(true);
  const [faf, setFaf] = useState({ enabled: false, rate: 0 });
  const [loyaltyRate, setLoyaltyRate] = useState(2);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [useCredit, setUseCredit] = useState(false);
  const [hoseLength, setHoseLength] = useState(30);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    address: params.get("address") || "", scheduled_date: "", notes: "",
  });
  const debRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const perLoad = zone ? (waterType === "pure" ? zone.pure_water : zone.town_water) : null;
  const subtotal = perLoad != null ? Math.round(perLoad * loads * 100) / 100 : null;
  const fafAmount = faf.enabled && faf.rate > 0 && subtotal != null ? Math.round(subtotal * faf.rate) / 100 : 0;
  const hoseFee = Math.max(0, hoseLength - 30);
  const gross = subtotal != null ? Math.round((subtotal + fafAmount + hoseFee) * 100) / 100 : null;
  const discount = coupon?.discount || 0;
  const afterCoupon = gross != null ? Math.max(0, Math.round((gross - discount) * 100) / 100) : null;
  const availableCredit = user?.store_credit || 0;
  const creditUsed = useCredit && afterCoupon != null ? Math.min(availableCredit, afterCoupon) : 0;
  const total = afterCoupon != null ? Math.max(0, Math.round((afterCoupon - creditUsed) * 100) / 100) : null;

  useEffect(() => {
    api.get("/service/config").then(({ data }) => {
      const en = data.pure_water_enabled !== false;
      setPureEnabled(en);
      if (!en) setWaterType("town");
      if (data.loyalty_rate != null) setLoyaltyRate(Number(data.loyalty_rate));
      setFaf({ enabled: !!data.faf_enabled, rate: Number(data.faf_rate || 0) });
    }).catch(() => {});
  }, []);

  useEffect(() => { setCoupon(null); }, [waterType, loads, zone, hoseLength]);

  const applyCoupon = async () => {
    if (!couponInput.trim() || subtotal == null) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim(), amount: gross });
      if (data.valid) { setCoupon({ code: data.code, discount: data.discount, label: data.label }); toast.success(`Coupon applied — ${data.label}`); }
      else { setCoupon(null); toast.error(data.reason || "Invalid coupon"); }
    } catch { toast.error("Could not check that coupon"); }
    finally { setCouponBusy(false); }
  };

  const onAddress = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, address: v }));
    setZone(null);
    if (debRef.current) clearTimeout(debRef.current);
    if (v.trim().length < 3) { setSuggestions([]); return; }
    setSearching(true);
    debRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geocode?q=${encodeURIComponent(v)}`);
        setSuggestions(data.suggestions || []);
      } catch { setSuggestions([]); }
      finally { setSearching(false); }
    }, 500);
  };

  const pick = (s) => {
    setForm((f) => ({ ...f, address: s.label.split(",")[0] + (s.suburb ? "" : "") }));
    setSuggestions([]);
    if (s.suburb) {
      setZone({ suburb: s.suburb, town_water: s.town_water, pure_water: s.pure_water });
      setForm((f) => ({ ...f, address: s.label }));
      toast.success(`We deliver to ${s.suburb}!`);
    } else {
      setZone(null);
      toast.error("That address is outside our service area.");
    }
  };

  useEffect(() => {
    api.get(`/availability?days=21`).then(({ data }) => setDays(data.days || [])).catch(() => setDays([]));
  }, []);

  const pickDay = (d) => {
    if (!d.available) return;
    setForm((f) => ({ ...f, scheduled_date: d.date }));
    setAvail(d);
  };

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      customer_name: f.customer_name || user.name || "",
      customer_email: f.customer_email || user.email || "",
      customer_phone: f.customer_phone || user.phone || "",
    }));
    if (user.preferred_payment) setPayment(user.preferred_payment);
    if (user.has_saved_card) {
      api.get("/account/cards").then(({ data }) => {
        const cards = data.cards || [];
        setSavedCards(cards);
        if (cards.length) { setSelectedCard(cards[0].id); setPayment("saved"); }
      }).catch(() => {});
    }
  }, [user]);

  const applySavedAddress = async (a) => {
    setForm((f) => ({ ...f, address: a.address }));
    setZone(null);
    try {
      const { data } = await api.get(`/geocode?q=${encodeURIComponent(a.address + ", " + a.suburb)}`);
      const list = data.suggestions || [];
      const match = list.find((s) => s.suburb && s.suburb.toLowerCase() === (a.suburb || "").toLowerCase()) || list.find((s) => s.suburb);
      if (match) pick(match);
      else toast.info("Pick your address from the suggestions to confirm pricing.");
    } catch { /* ignore */ }
  };

  const submit = async () => {
    if (!zone) { toast.error("Enter your address and pick a match to get pricing"); return; }
    if (!form.customer_name || !form.customer_email || !form.customer_phone || !form.address || !form.scheduled_date) {
      toast.error("Please fill in all required fields"); return;
    }
    if (avail && (avail.blocked || loads > avail.remaining)) {
      toast.error(avail.blocked ? "Bookings are closed that day" : `Only ${avail.remaining} load(s) left that day`); return;
    }
    setLoading(true);
    try {
      const pm = payment === "saved" ? "card" : payment;
      const { data: order } = await api.post("/orders", {
        ...form, suburb: zone.suburb, service_type: service, water_type: waterType, loads, payment_method: pm, coupon_code: coupon?.code || null, credit_applied: creditUsed, hose_length: hoseLength,
      });
      if (order.payment_status === "paid") {
        toast.success("Paid with your store credit");
        navigate(`/payment/success?order_id=${order.id}`);
        return;
      }
      if (payment === "saved") {
        await api.post("/payments/pay-saved", { order_id: order.id, payment_method_id: selectedCard });
        toast.success("Paid with your saved card");
        navigate(`/payment/success?order_id=${order.id}`);
      } else if (payment === "card") {
        const { data } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      } else {
        toast.success("Order placed! We'll be in touch.");
        navigate(`/payment/success?order_id=${order.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not place order");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/" className="text-sm text-slate-500 hover:text-blue-600 flex items-center gap-1" data-testid="book-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="font-heading text-3xl font-extrabold text-slate-900">Book Your Water Delivery</motion.h1>
        <p className="text-slate-500 mt-1 mb-4">11,000L per load, North-West Auckland. Start typing your address for an instant price.</p>

        {loyaltyRate > 0 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full pl-2.5 pr-4 py-1.5 mb-8" data-testid="loyalty-badge">
            <span className="flex items-center justify-center w-6 h-6 rounded-full brand-gradient text-white shrink-0">
              <Gift className="w-3.5 h-3.5" />
            </span>
            <span className="text-sm font-semibold text-blue-800">
              Earn {loyaltyRate}% back on every order
              {!user && <Link to="/login" className="text-blue-600 font-bold hover:underline ml-1" data-testid="loyalty-badge-signin">— sign in to start</Link>}
            </span>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
          <div className="space-y-8">
            {/* ADDRESS AUTOCOMPLETE */}
            <Section title="1. Your delivery address">
              {user && (user.addresses || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3" data-testid="saved-address-chips">
                  {user.addresses.map((a) => (
                    <button key={a.id} onClick={() => applySavedAddress(a)} data-testid={`saved-address-${a.id}`}
                      className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5 hover:bg-blue-100 transition-colors">
                      <MapPin className="w-3.5 h-3.5" /> {a.label || a.suburb || "Saved"}
                    </button>
                  ))}
                </div>
              )}
              <div className="relative">
                <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
                  {searching ? <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" /> : <Search className="w-4 h-4 text-slate-400 shrink-0" />}
                  <input value={form.address} onChange={onAddress} data-testid="book-address"
                    placeholder="Start typing your street or suburb..." autoComplete="off"
                    className="w-full py-3 outline-none text-sm text-slate-800 bg-transparent" />
                </div>
                {suggestions.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden" data-testid="address-suggestions">
                    {suggestions.map((s, i) => (
                      <button key={i} onClick={() => pick(s)} data-testid={`address-suggestion-${i}`}
                        className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-slate-50 last:border-0 flex items-center justify-between gap-2">
                        <span className="text-sm text-slate-700 truncate">{s.label}</span>
                        {s.suburb ? <span className="text-xs font-semibold text-green-600 shrink-0">${s.town_water}+</span>
                          : <span className="text-xs text-red-400 shrink-0">Out of area</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {zone && (
                <div className="mt-3 flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl p-3 text-sm" data-testid="book-in-area">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-green-800 font-semibold">{zone.suburb}</span>
                  <span className="text-green-700">— Town ${zone.town_water} · Pure ${zone.pure_water} per load</span>
                </div>
              )}
            </Section>

            {/* SERVICE */}
            <Section title="2. What do you need water for?">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {SERVICES.map((s) => (
                  <button key={s.type} onClick={() => setService(s.type)}
                    className={`rounded-xl border p-4 flex flex-col items-center gap-2 transition-all ${service === s.type ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 bg-white hover:border-blue-300"}`}
                    data-testid={`book-service-${s.type}`}>
                    <s.icon className={`w-6 h-6 ${service === s.type ? "text-blue-600" : "text-slate-400"}`} />
                    <span className="text-xs font-semibold text-slate-700">{s.label}</span>
                  </button>
                ))}
              </div>
            </Section>

            {/* WATER + LOADS */}
            <Section title="3. Water type & loads">
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                {WATER.map((w) => {
                  const disabled = w.type === "pure" && !pureEnabled;
                  return (
                  <button key={w.type} disabled={disabled} onClick={() => !disabled && setWaterType(w.type)}
                    className={`rounded-xl border p-4 text-left transition-all ${disabled ? "border-slate-200 bg-slate-50 opacity-70 cursor-not-allowed" : waterType === w.type ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 bg-white hover:border-blue-300"}`}
                    data-testid={`book-water-${w.type}`}>
                    <div className="flex items-center gap-2 font-heading font-bold text-slate-900">
                      <Droplets className={`w-5 h-5 ${waterType === w.type ? "text-blue-600" : "text-slate-400"}`} /> {w.label}
                      {disabled && <span className="ml-auto text-[10px] font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-full" data-testid="pure-out-of-stock">OUT OF STOCK</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{disabled ? "Temporarily unavailable" : w.note}</div>
                    {zone && !disabled && <div className="font-display text-lg font-bold text-blue-600 mt-2">${w.type === "pure" ? zone.pure_water : zone.town_water}<span className="text-xs font-medium text-slate-400"> /load</span></div>}
                  </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between border border-slate-200 rounded-xl p-4 bg-white">
                <div>
                  <div className="font-semibold text-slate-800 text-sm">Number of loads</div>
                  <div className="text-xs text-slate-500">11,000L each · max 8 per day</div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => setLoads(Math.max(1, loads - 1))} className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center hover:border-blue-400" data-testid="loads-minus"><Minus className="w-4 h-4" /></button>
                  <span className="font-display text-2xl font-extrabold text-slate-900 w-8 text-center" data-testid="loads-count">{loads}</span>
                  <button onClick={() => setLoads(Math.min(8, loads + 1))} className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center hover:border-blue-400" data-testid="loads-plus"><Plus className="w-4 h-4" /></button>
                </div>
              </div>
              <HosePicker value={hoseLength} onChange={setHoseLength} fee={hoseFee} />
            </Section>

            {/* SCHEDULE */}
            <Section title="4. Choose a delivery day">
              <p className="text-sm text-slate-500 -mt-2 mb-3">Pick any available day. Each day fits up to 8 loads — we'll show how many are still open. No time slots; we deliver across the day.</p>
              <div className="flex items-center gap-2 mb-4 text-xs font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 w-fit" data-testid="holiday-notice">
                <Info className="w-4 h-4 text-blue-500 shrink-0" /> Closed on Sundays &amp; NZ public holidays. Same-day cut-off: 12pm Sat, 3pm Mon–Fri.
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="day-picker">
                {days.length === 0 && <div className="col-span-full text-sm text-slate-400 py-6 text-center">Loading available days…</div>}
                {days.map((d) => {
                  const dt = parseDay(d.date);
                  const selected = form.scheduled_date === d.date;
                  const disabled = !d.available;
                  return (
                    <button key={d.date} onClick={() => pickDay(d)} disabled={disabled} data-testid={`day-${d.date}`}
                      className={`relative rounded-xl border p-3 text-left transition-all ${selected ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100" : disabled ? "border-slate-100 bg-slate-50 cursor-not-allowed opacity-60" : "border-slate-200 bg-white hover:border-blue-300"}`}>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{dt.rel || dt.weekday}</div>
                      <div className="font-heading font-bold text-slate-900 leading-tight">{dt.day} {dt.month}</div>
                      <div className="mt-1.5">
                        {d.available ? (
                          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${d.remaining <= 2 ? "text-amber-600" : "text-green-600"}`}><CheckCircle2 className="w-3 h-3" /> {d.remaining} of {d.capacity} left</span>
                        ) : (
                          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${d.reason === "full" ? "text-red-400" : "text-slate-400"}`} title={d.holiday_name || ""}><XCircle className="w-3 h-3" /> {CLOSED_LABEL[d.reason] || "Closed"}</span>
                        )}
                      </div>
                      {selected && <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-blue-600" />}
                    </button>
                  );
                })}
              </div>
              {avail && loads > avail.remaining && avail.remaining > 0 && (
                <div className="mt-3 flex items-center gap-2 rounded-xl p-3 text-sm bg-amber-50 text-amber-700 border border-amber-200" data-testid="book-loads-warning">
                  <XCircle className="w-4 h-4 shrink-0" /> You've selected {loads} loads but only {avail.remaining} remain on this day. Reduce loads or pick another day.
                </div>
              )}
              <div className="mt-4">
                <Field label="Notes (optional)" value={form.notes} onChange={set("notes")} testid="book-notes" placeholder="Gate code, access info..." />
              </div>
            </Section>

            {/* CONTACT */}
            <Section title="5. Your contact details">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field icon={User} label="Full name *" value={form.customer_name} onChange={set("customer_name")} testid="book-name" placeholder="Sarah Mitchell" />
                <Field label="Phone *" value={form.customer_phone} onChange={set("customer_phone")} testid="book-phone" placeholder="021 234 5678" />
                <Field label="Email *" type="email" value={form.customer_email} onChange={set("customer_email")} testid="book-email" placeholder="you@example.com" />
              </div>
            </Section>

            {/* PAYMENT */}
            <Section title="6. Payment method">
              {savedCards.length > 0 && (
                <div className="space-y-2 mb-3">
                  {savedCards.map((c) => (
                    <button key={c.id} onClick={() => { setPayment("saved"); setSelectedCard(c.id); }}
                      className={`w-full rounded-xl border p-4 flex items-center gap-3 transition-all ${payment === "saved" && selectedCard === c.id ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 bg-white hover:border-blue-300"}`}
                      data-testid={`book-payment-saved-${c.id}`}>
                      <CreditCard className="w-6 h-6 text-blue-600" />
                      <div className="text-left"><div className="font-semibold text-slate-800 text-sm capitalize">{c.brand} •••• {c.last4}</div><div className="text-xs text-slate-500">One-tap saved card</div></div>
                      <CheckCircle2 className={`w-5 h-5 ml-auto ${payment === "saved" && selectedCard === c.id ? "text-blue-600" : "text-transparent"}`} />
                    </button>
                  ))}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <button onClick={() => setPayment("card")} className={`rounded-xl border p-4 flex items-center gap-3 transition-all ${payment === "card" ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 bg-white hover:border-blue-300"}`} data-testid="book-payment-card">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                  <div className="text-left"><div className="font-semibold text-slate-800 text-sm">{savedCards.length > 0 ? "Pay with a new card" : "Pay online now"}</div><div className="text-xs text-slate-500">Secure card payment</div></div>
                </button>
                <button onClick={() => setPayment("cod")} className={`rounded-xl border p-4 flex items-center gap-3 transition-all ${payment === "cod" ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 bg-white hover:border-blue-300"}`} data-testid="book-payment-cod">
                  <Banknote className="w-6 h-6 text-green-600" />
                  <div className="text-left"><div className="font-semibold text-slate-800 text-sm">Pay on delivery</div><div className="text-xs text-slate-500">Cash or card to driver</div></div>
                </button>
              </div>
              {!user && <p className="text-xs text-slate-400 mt-3"><Link to="/login" className="text-blue-600 font-semibold hover:underline">Sign in or create an account</Link> to save your details and card for faster checkout next time.</p>}
            </Section>
          </div>

          {/* SUMMARY */}
          <div className="lg:sticky lg:top-6 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><Droplets className="w-5 h-5 text-blue-600" /> Order summary</h3>
            <Row label="Service" value={SERVICES.find((s) => s.type === service)?.label} />
            <Row label="Water" value={WATER.find((w) => w.type === waterType)?.label} />
            <Row label="Suburb" value={zone?.suburb || "—"} />
            <Row label="Loads" value={`${loads} × 11,000L`} />
            <Row label="Hose length" value={`${hoseLength}m`} />
            <Row label="Payment" value={payment === "saved" ? "Saved card" : payment === "card" ? "Card (online)" : "On delivery"} />
            <div className="border-t border-slate-100 my-4" />
            <div className="mb-3">
              {!coupon ? (
                <div className="flex gap-2">
                  <input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="book-coupon-input"
                    className="flex-1 min-w-0 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-500 uppercase" />
                  <button onClick={applyCoupon} disabled={couponBusy || !couponInput.trim() || subtotal == null} data-testid="book-coupon-apply-btn"
                    className="px-4 rounded-xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-40">{couponBusy ? "…" : "Apply"}</button>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-3 py-2" data-testid="book-coupon-applied">
                  <span className="text-sm font-semibold text-green-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> {coupon.code} · {coupon.label}</span>
                  <button onClick={() => { setCoupon(null); setCouponInput(""); }} className="text-xs text-slate-500 hover:underline" data-testid="book-coupon-remove-btn">Remove</button>
                </div>
              )}
            </div>
            {subtotal != null && <Row label="Subtotal" value={`$${subtotal}`} />}
            {fafAmount > 0 && <div className="flex items-center justify-between py-1.5 text-sm"><span className="text-slate-600">Fuel adjustment ({faf.rate}%)</span><span className="font-semibold text-slate-700" data-testid="book-faf">+${fafAmount}</span></div>}
            {hoseFee > 0 && <div className="flex items-center justify-between py-1.5 text-sm"><span className="text-slate-600">Extra hose ({hoseFee}m × $1)</span><span className="font-semibold text-slate-700" data-testid="book-hose-fee">+${hoseFee}</span></div>}
            {discount > 0 && <div className="flex items-center justify-between py-1.5 text-sm"><span className="text-green-600">Discount</span><span className="font-semibold text-green-600" data-testid="book-discount">-${discount}</span></div>}
            {user && availableCredit > 0 && afterCoupon != null && (
              <label className="flex items-center justify-between gap-2 py-1.5 cursor-pointer" data-testid="book-use-credit-row">
                <span className="text-sm text-slate-600 flex items-center gap-2">
                  <input type="checkbox" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} data-testid="book-use-credit-checkbox" className="w-4 h-4 accent-blue-600" />
                  Use store credit
                </span>
                <span className="text-xs font-semibold text-slate-500">${availableCredit} available</span>
              </label>
            )}
            {creditUsed > 0 && <div className="flex items-center justify-between py-1.5 text-sm"><span className="text-blue-600">Store credit</span><span className="font-semibold text-blue-600" data-testid="book-credit">-${creditUsed}</span></div>}
            <div className="flex items-center justify-between mb-1 mt-1">
              <span className="text-slate-500">Total</span>
              <span className="font-display text-3xl font-extrabold text-slate-900" data-testid="book-total">{total != null ? `$${total}` : "—"}</span>
            </div>
            <p className="text-xs text-slate-400 mb-5">NZD, GST inclusive. Delivery included.</p>
            <button onClick={submit} disabled={loading || !zone || (avail && !avail.available)}
              className="brand-gradient btn-shine text-white font-semibold w-full py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="confirm-booking-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : payment === "cod" ? "Confirm Booking" : "Pay & Confirm"}
            </button>
            {!zone && <p className="text-xs text-center text-slate-400 mt-2">Enter a serviced address to see the price.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

const parseDay = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((dt - today) / 86400000);
  const rel = diff === 0 ? "Today" : diff === 1 ? "Tomorrow" : null;
  return {
    weekday: dt.toLocaleDateString("en-NZ", { weekday: "short" }),
    day: dt.getDate(),
    month: dt.toLocaleDateString("en-NZ", { month: "short" }),
    rel,
  };
};

const HosePicker = ({ value, onChange, fee }) => (
  <div className="mt-4 border border-slate-200 rounded-xl p-4 bg-white" data-testid="hose-picker">
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="font-semibold text-slate-800 text-sm">Hose length needed</div>
        <div className="text-xs text-slate-500">From the truck to your tank or pool · first 30m free, then $1 per extra metre (max 100m)</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <input type="number" min={1} max={100} value={value} data-testid="hose-length-input"
          onChange={(e) => onChange(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))}
          className="w-16 border border-slate-200 rounded-lg px-2 py-1.5 text-center font-display font-bold text-slate-900 outline-none focus:border-blue-500" />
        <span className="text-sm text-slate-500">m</span>
      </div>
    </div>
    <input type="range" min={1} max={100} value={value} onChange={(e) => onChange(Number(e.target.value))}
      className="w-full mt-3 accent-blue-600" data-testid="hose-length-slider" />
    <div className={`text-xs font-semibold mt-1 ${fee > 0 ? "text-amber-600" : "text-green-600"}`} data-testid="hose-fee-note">
      {fee > 0 ? `+$${fee} for ${fee}m of extra hose` : "No hose charge — within the free 30m"}
    </div>
  </div>
);
const Section = ({ title, children }) => (<div><h2 className="font-heading font-bold text-slate-900 mb-4">{title}</h2>{children}</div>);
const Row = ({ label, value }) => (<div className="flex items-center justify-between py-1.5 text-sm"><span className="text-slate-500">{label}</span><span className="font-medium text-slate-800">{value}</span></div>);
const Field = ({ icon: Icon, label, testid, ...props }) => (
  <div>
    <label className="text-xs font-medium text-slate-600 mb-1.5 block">{label}</label>
    <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
      {Icon && <Icon className="w-4 h-4 text-slate-400 shrink-0" />}
      <input {...props} data-testid={testid} className="w-full py-2.5 outline-none text-sm text-slate-800 bg-transparent" />
    </div>
  </div>
);
